import { axiosInstance } from './authService';

// Normalize API breakdown objects into a consistent shape consumed by the UI
export const normalizeBreakdown = (b) => {
  if (!b || typeof b !== 'object') return b;

  // Determine IDs
  const id = b.id ?? b.breakdownId ?? b.breakdownID ?? b.BreakdownId ?? b.BreakdownID;
  const breakdownId = b.breakdownId ?? b.breakdownID ?? b.BreakdownId ?? b.BreakdownID ?? id;

  // Determine reference
  const breakdownReference =
    b.breakdownReference ?? b.reference ?? b.breakdownRef ?? b.breakdownNumber ?? b.referenceNumber ?? b.BreakdownReference;

  // Vehicle + company
  const registrationNumber =
    b.registrationNumber ?? b.vehicleRegistration ?? b.registration ?? b.regNumber ?? b.vehicleReg ?? b.regNo;
  const companyName =
    b.companyName ?? b.ownerCompanyName ?? b.customerCompanyName ?? b.company ?? b.clientCompanyName ?? b.customerName;

  // Dates
  const dateTimeOffset =
    b.dateTimeOffset ?? b.createdDate ?? b.createdOn ?? b.createdAt ?? b.breakdownDate ?? b.date;

  // Address/Location
  const breakdownAddress = b.breakdownAddress ?? b.address ?? b.location ?? b.addressLine ?? b.breakdownLocation;

  // Status fields
  const statusDescription =
    b.statusDescription ?? b.breakdownStatusDescription ?? b.breakdownStatus ?? b.status;
  const breakdownMechanicStatus =
    b.breakdownMechanicStatus ?? b.breakdownMechanicStatusId ?? b.mechanicStatus ?? null;

  // Vehicle details
  const vinNumber = b.vinNumber ?? b.vin ?? b.VIN;
  const fleetNumber = b.fleetNumber ?? b.fleet ?? b.fleetNo;
  const make = b.make ?? b.vehicleMake;
  const model = b.model ?? b.vehicleModel;
  const year = b.year ?? b.vehicleYear;

  // Description
  const description = b.description ?? b.faultDescription ?? b.details ?? b.breakdownDescription;

  return {
    ...b,
    id,
    breakdownId,
    breakdownReference,
    registrationNumber,
    companyName,
    dateTimeOffset,
    breakdownAddress,
    statusDescription,
    breakdownMechanicStatus,
    vinNumber,
    fleetNumber,
    make,
    model,
    year,
    description
  };
};

const breakdownService = {
  // Fetch breakdowns assigned to the mechanic
  getAssignedBreakdowns: async () => {
    try {
      const response = await axiosInstance.get('/BreakdownMechanics/getBreakdownsAssignedToMechanic');
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.breakdowns)
          ? data.breakdowns
          : Array.isArray(data?.data)
            ? data.data
            : [];
      return list.map(normalizeBreakdown);
    } catch (error) {
      console.error('Error fetching assigned breakdowns:', error);
      return {
        success: false,
        breakdowns: [],
        message: error.response?.data?.message || 'Failed to fetch assigned breakdowns'
      };
    }
  },

  // History breakdowns for dashboard (resolved/closed etc.)
  getHistoryBreakdowns: async ({ limit, fromDate, toDate, breakdownReference } = {}) => {
    try {
      const params = {};
      if (limit !== undefined && limit !== 'all') params.limit = limit;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (breakdownReference && String(breakdownReference).trim()) params.breakdownReference = String(breakdownReference).trim();

      const response = await axiosInstance.get('/Breakdowns/HistoryBreakdownDashboardList', {
        params
      });
      let data = response.data;
      // Some servers return text/plain JSON
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          data = [];
        }
      }
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.breakdowns)
          ? data.breakdowns
          : Array.isArray(data?.data)
            ? data.data
            : [];
      return list.map(normalizeBreakdown);
    } catch (error) {
      console.error('Error fetching history breakdowns:', error);
      return [];
    }
  },

  getVisibleBreakdowns: async ({ statuses } = {}) => {
    try {
      const params = {};
      if (Array.isArray(statuses) && statuses.length > 0) {
        params.statuses = statuses
          .map((s) => Number(s))
          .filter((n) => Number.isInteger(n));
      }

      const response = await axiosInstance.get('/Breakdowns/ActiveBreakdownDashboardList', {
        params,
        paramsSerializer: (p) => {
          const searchParams = new URLSearchParams();
          Object.keys(p || {}).forEach((key) => {
            const value = p[key];
            if (Array.isArray(value)) {
              value.forEach((v) => {
                if (v !== undefined && v !== null) searchParams.append(key, v);
              });
            } else if (value !== undefined && value !== null) {
              searchParams.append(key, value);
            }
          });
          return searchParams.toString();
        }
      });
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.breakdowns)
          ? data.breakdowns
          : Array.isArray(data?.data)
            ? data.data
            : [];
      return list.map(normalizeBreakdown);
    } catch (error) {
      console.error('Error fetching visible breakdowns:', error);
      return {
        success: false,
        breakdowns: [],
        message: error.response?.data?.message || 'Failed to fetch visible breakdowns'
      };
    }
  },

  // Format date to a more readable format
  formatDate: (dateString) => {
    if (!dateString || dateString === '0001-01-01T00:00:00+00:00') {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-ZA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date);
    } catch {
      return dateString;
    }
  },
  
  // Calculate elapsed time from dateTimeOffset to now
  calculateElapsedTime: (dateString) => {
    if (!dateString || dateString === '0001-01-01T00:00:00+00:00') {
      return '';
    }
    
    try {
      const startDate = new Date(dateString);
      const currentDate = new Date();
      
      const diffInMs = currentDate - startDate;
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      
      if (diffInHours < 24) {
        return `${diffInHours} hours ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays} days ago`;
      }
    } catch {
      return '';
    }
  },
  
  // Get vehicle display string
  getVehicleDisplayString: (breakdown) => {
    const { make, model, year } = breakdown;
    const parts = [];
    
    if (make) parts.push(make);
    if (model) parts.push(model);
    if (year) parts.push(year);
    
    return parts.join(' | ');
  },
  
  // Get a specific breakdown by ID
  getBreakdownById: async (id) => {
    try {
      // This would be replaced with the actual endpoint when available
      const response = await axiosInstance.get(`/BreakdownMechanics/getBreakdownDetails/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching breakdown details for ID: ${id}`, error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch breakdown details'
      };
    }
  }
};

export default breakdownService;
